import React from 'react';

/**
 * ListingTypeBadge - Consistent listing type badge styling
 * 
 * Colors are standardized across the entire app:
 * - sale: blue-600
 * - rent: purple-600
 * - trade: teal-600
 * - service: indigo-600
 * - yard_sale: amber-500
 * - auction: rose-600
 * - bulk: pink-600
 * - giveaway: cyan-500
 * 
 * @param {string} type - Listing type (sale, rent, trade, service, yard_sale, auction, bulk, giveaway)
 * @param {string} size - Size variant: 'sm', 'md', 'lg'
 * @param {string} className - Additional classes
 */
const ListingTypeBadge = ({ type, size = 'md', className = '' }) => {
  const config = {
    sale: { label: 'For Sale', bg: 'bg-blue-600', text: 'text-white' },
    rent: { label: 'For Rent', bg: 'bg-purple-600', text: 'text-white' },
    trade: { label: 'For Trade', bg: 'bg-teal-600', text: 'text-white' },
    service: { label: 'Service', bg: 'bg-indigo-600', text: 'text-white' },
    yard_sale: { label: 'Yard Sale', bg: 'bg-amber-500', text: 'text-white' },
    auction: { label: 'Auction', bg: 'bg-rose-600', text: 'text-white' },
    bulk: { label: 'Bulk', bg: 'bg-pink-600', text: 'text-white' },
    giveaway: { label: 'Giveaway', bg: 'bg-cyan-500', text: 'text-white' }
  };
  
  const badge = config[type] || { 
    label: type?.replace('_', ' ') || 'Unknown', 
    bg: 'bg-gray-500', 
    text: 'text-white' 
  };
  
  const sizeClasses = {
    sm: 'px-1.5 py-0.5 text-[10px]',
    md: 'px-2 py-0.5 text-xs',
    lg: 'px-3 py-1 text-sm'
  };
  
  return (
    <span 
      className={`${badge.bg} ${badge.text} ${sizeClasses[size]} font-medium rounded-full ${className}`}
      data-testid={`badge-listing-type-${type}`}
    >
      {badge.label}
    </span>
  );
};

/**
 * Get listing type colors for use in other contexts (icons, backgrounds)
 * Returns Tailwind color classes
 */
export const getListingTypeColors = (type) => {
  const colors = {
    sale: { bg: 'bg-blue-500', text: 'text-blue-500', bgLight: 'bg-blue-500/10' },
    rent: { bg: 'bg-purple-500', text: 'text-purple-500', bgLight: 'bg-purple-500/10' },
    trade: { bg: 'bg-teal-500', text: 'text-teal-500', bgLight: 'bg-teal-500/10' },
    service: { bg: 'bg-indigo-500', text: 'text-indigo-500', bgLight: 'bg-indigo-500/10' },
    yard_sale: { bg: 'bg-amber-500', text: 'text-amber-500', bgLight: 'bg-amber-500/10' },
    auction: { bg: 'bg-rose-500', text: 'text-rose-500', bgLight: 'bg-rose-500/10' },
    bulk: { bg: 'bg-pink-500', text: 'text-pink-500', bgLight: 'bg-pink-500/10' },
    giveaway: { bg: 'bg-cyan-500', text: 'text-cyan-500', bgLight: 'bg-cyan-500/10' }
  };
  
  return colors[type] || { bg: 'bg-gray-500', text: 'text-gray-500', bgLight: 'bg-gray-500/10' };
};

/**
 * ListingTypeHeader - Colored header bar for listing cards
 * Displays the listing type as a full-width colored bar ABOVE the image
 */
export const ListingTypeHeader = ({ type, className = '' }) => {
  const config = {
    sale: { label: 'For Sale', bg: 'bg-blue-500' },
    fixed_price: { label: 'Fixed Price', bg: 'bg-gray-700' },
    rent: { label: 'For Rent', bg: 'bg-purple-500' },
    trade: { label: 'For Trade', bg: 'bg-emerald-500' },
    service: { label: 'Service', bg: 'bg-indigo-500' },
    yard_sale: { label: 'Yard Sale', bg: 'bg-orange-500' },
    auction: { label: 'Auction', bg: 'bg-amber-500' },
    bulk: { label: 'Bulk', bg: 'bg-pink-500' },
    giveaway: { label: 'Giveaway', bg: 'bg-cyan-500' }
  };
  
  const header = config[type] || { 
    label: type?.replace('_', ' ') || 'Listing', 
    bg: 'bg-gray-700' // Darker gray for better contrast
  };
  
  return (
    <div 
      className={`${header.bg} text-white text-center py-2.5 text-sm font-bold uppercase tracking-wider ${className}`}
      data-testid={`listing-type-header-${type}`}
    >
      {header.label}
    </div>
  );
};

export default ListingTypeBadge;
